import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { SIGNAL_OUT_OF_TESTING_SCOPE_ERROR } from '@terra/shared-asset-processing';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import {
  useAppTargetId,
  useTargetId,
  useTargetType,
} from '../store/projectStore';
import { useExecutionsStore } from '../store/executionsStore';
import {
  isAppTarget,
  isNetworkAttackTarget,
  supportsPortalSignalAttack,
} from '../utils/target-scope.utils';
import { getAuthHeaders } from '../utils/authHeaders';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { useBulkNeoRun } from './api/useBulkNeoRun';
import { runAttackForSignal } from './api/useAttackRun';
import type { OpenMainContentTabInput } from '../store/layoutStore';
import { useMainContentTabs } from '../store/layoutStore';

type NeoRunResponse = {
  success: boolean;
  threadId: string | null;
  executionId: string | null;
  alreadyRunning?: boolean;
  error?: string;
};

export function useSignalAttackRun() {
  const tSignalsBulk = useTranslations('signals.bulkActions');
  const targetId = useTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const { navigateInActiveTab } = useMainContentTabs();
  const bulkNeoRun = useBulkNeoRun();
  const startNeoAttack = useExecutionsStore((state) => state.startNeoAttack);
  const addNeoAttack = useExecutionsStore((state) => state.addNeoAttack);
  const updateNeoAttackStatus = useExecutionsStore(
    (state) => state.updateNeoAttackStatus,
  );
  const startNeoAttacks = useExecutionsStore((state) => state.startNeoAttacks);
  const failNeoAttacks = useExecutionsStore((state) => state.failNeoAttacks);
  const applyBulkNeoAttackResults = useExecutionsStore(
    (state) => state.applyBulkNeoAttackResults,
  );
  const addRunningExecution = useExecutionsStore(
    (state) => state.addRunningExecution,
  );
  const removeRunningExecution = useExecutionsStore(
    (state) => state.removeRunningExecution,
  );
  const isRunningExecution = useExecutionsStore((state) => state.isRunning);

  const canStartSignalAttack = supportsPortalSignalAttack({
    targetType,
    targetId: targetId || undefined,
    appTargetId,
  });

  const getAgentExecutionTabData = useCallback(
    (executionId: string): OpenMainContentTabInput => ({
      id: `agent-execution-${executionId}`,
      title: tSignalsBulk('agentExecutionTabTitle'),
      type: 'agent-execution',
      data: { executionId },
    }),
    [tSignalsBulk],
  );

  const runApplicationNeoAttack = useCallback(
    async ({
      signalId,
      applicationId,
    }: {
      signalId: string;
      applicationId: string;
    }): Promise<boolean> => {
      startNeoAttack({ signalId, applicationId });

      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(
          `${getCurrentGatewayUrl()}/ai/agents/neo/run`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders,
            },
            body: JSON.stringify({
              signalId,
              applicationId,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to start Neo');
        }

        const data = (await response.json()) as NeoRunResponse;

        if (data.error === SIGNAL_OUT_OF_TESTING_SCOPE_ERROR) {
          updateNeoAttackStatus({ signalId, status: 'failed' });
          toasterApi.error({
            title: tSignalsBulk('neoOutOfTestingScopeTitle'),
            description: tSignalsBulk('neoOutOfTestingScopeDescription'),
          });

          return false;
        }

        if (data.alreadyRunning) {
          toasterApi.info({
            title: tSignalsBulk('neoQueuedTitle'),
            description: tSignalsBulk('neoQueuedDescription'),
          });

          return false;
        }

        if (data.threadId) {
          addNeoAttack({
            signalId,
            threadId: data.threadId,
            executionId: data.executionId,
            applicationId,
          });
          toasterApi.success({
            title: tSignalsBulk('neoStartedSingleTitle'),
            description: tSignalsBulk('neoStartedSingleDescription'),
          });

          return true;
        }

        updateNeoAttackStatus({ signalId, status: 'failed' });
        toasterApi.info({
          title: tSignalsBulk('neoQueuedTitle'),
          description: tSignalsBulk('neoQueuedDescription'),
        });

        return false;
      } catch (error: unknown) {
        console.error('Failed to start Neo:', error);
        updateNeoAttackStatus({ signalId, status: 'failed' });
        toasterApi.error({
          title: tSignalsBulk('neoFailedTitle'),
          description: tSignalsBulk('neoFailedDescription'),
        });

        return false;
      }
    },
    [addNeoAttack, startNeoAttack, tSignalsBulk, updateNeoAttackStatus],
  );

  const runNetworkAttack = useCallback(
    async ({
      signalId,
      networkTargetId,
      networkTargetType,
      openExecutionTab,
    }: {
      signalId: string;
      networkTargetId: string;
      networkTargetType: TargetTypeType;
      openExecutionTab?: boolean;
    }): Promise<boolean> => {
      addRunningExecution(signalId);

      try {
        const data = await runAttackForSignal({
          targetId: networkTargetId,
          targetType: networkTargetType,
          signalId,
        });

        if (data.success && data.executionId) {
          toasterApi.success({
            title: tSignalsBulk('neoStartedSingleTitle'),
            description: tSignalsBulk('neoStartedSingleDescription'),
          });
          if (openExecutionTab) {
            navigateInActiveTab(getAgentExecutionTabData(data.executionId));
          }

          return true;
        }

        toasterApi.info({
          title: tSignalsBulk('neoQueuedTitle'),
          description: data.message ?? tSignalsBulk('neoQueuedDescription'),
        });

        return false;
      } catch (error: unknown) {
        console.error('Failed to start network attack:', error);
        toasterApi.error({
          title: tSignalsBulk('neoFailedTitle'),
          description: tSignalsBulk('neoFailedDescription'),
        });

        return false;
      } finally {
        removeRunningExecution(signalId);
      }
    },
    [
      addRunningExecution,
      getAgentExecutionTabData,
      navigateInActiveTab,
      removeRunningExecution,
      tSignalsBulk,
    ],
  );

  const startSignalAttack = useCallback(
    async ({
      signalId,
      isEligible = true,
      openExecutionTab = false,
    }: {
      signalId: string;
      isEligible?: boolean;
      openExecutionTab?: boolean;
    }): Promise<boolean> => {
      if (!canStartSignalAttack || !isEligible || !targetType || !targetId) {
        return false;
      }

      if (isAppTarget(targetType)) {
        if (!appTargetId) {
          return false;
        }

        return runApplicationNeoAttack({
          signalId,
          applicationId: appTargetId,
        });
      }

      if (isNetworkAttackTarget(targetType)) {
        return runNetworkAttack({
          signalId,
          networkTargetId: targetId,
          networkTargetType: targetType,
          openExecutionTab,
        });
      }

      return false;
    },
    [
      appTargetId,
      canStartSignalAttack,
      runApplicationNeoAttack,
      runNetworkAttack,
      targetId,
      targetType,
    ],
  );

  const startBulkSignalAttack = useCallback(
    async ({ signalIds }: { signalIds: string[] }): Promise<boolean> => {
      if (
        !canStartSignalAttack ||
        !targetType ||
        !targetId ||
        signalIds.length === 0
      ) {
        return false;
      }

      if (isAppTarget(targetType)) {
        if (!appTargetId) {
          return false;
        }

        startNeoAttacks({ signalIds, applicationId: appTargetId });

        try {
          const data = await bulkNeoRun.mutateAsync({
            applicationId: appTargetId,
            signalIds,
          });
          const { succeeded, failed, queued } = applyBulkNeoAttackResults({
            applicationId: appTargetId,
            results: data.results,
          });
          const outOfScopeFailures = data.results.filter(
            (result) => result.error === SIGNAL_OUT_OF_TESTING_SCOPE_ERROR,
          ).length;

          if (succeeded > 0) {
            toasterApi.success({
              title: tSignalsBulk('neoStartedTitle', { count: succeeded }),
              description:
                failed > 0
                  ? tSignalsBulk('neoStartedPartialDescription', {
                      count: failed,
                    })
                  : queued > 0
                    ? tSignalsBulk('neoStartedSingleDescription')
                    : tSignalsBulk('neoStartedDescription'),
            });
          }

          if (queued > 0) {
            toasterApi.info({
              title: tSignalsBulk('neoQueuedTitle'),
              description: tSignalsBulk('neoQueuedBatchDescription', {
                count: queued,
              }),
            });
          }

          if (failed > 0 && succeeded === 0) {
            toasterApi.error({
              title:
                outOfScopeFailures === failed
                  ? tSignalsBulk('neoOutOfTestingScopeTitle')
                  : tSignalsBulk('neoFailedTitle'),
              description:
                outOfScopeFailures === failed
                  ? tSignalsBulk('neoOutOfTestingScopeDescription')
                  : tSignalsBulk('neoFailedDescription'),
            });
          }

          return succeeded > 0;
        } catch (error: unknown) {
          failNeoAttacks(signalIds);
          console.error('Failed to start Neo for selected signals:', error);
          toasterApi.error({
            title: tSignalsBulk('neoFailedTitle'),
            description: tSignalsBulk('neoFailedDescription'),
          });

          return false;
        }
      }

      if (isNetworkAttackTarget(targetType)) {
        const results = await Promise.allSettled(
          signalIds.map((signalId) =>
            runAttackForSignal({
              targetId,
              targetType,
              signalId,
            }),
          ),
        );

        let succeeded = 0;
        let failed = 0;

        for (const result of results) {
          if (
            result.status === 'fulfilled' &&
            result.value.success &&
            result.value.executionId
          ) {
            succeeded += 1;
          } else {
            failed += 1;
          }
        }

        if (succeeded > 0) {
          toasterApi.success({
            title: tSignalsBulk('neoStartedTitle', { count: succeeded }),
            description:
              failed > 0
                ? tSignalsBulk('neoStartedPartialDescription', {
                    count: failed,
                  })
                : tSignalsBulk('neoStartedDescription'),
          });
        }

        if (failed > 0 && succeeded === 0) {
          toasterApi.error({
            title: tSignalsBulk('neoFailedTitle'),
            description: tSignalsBulk('neoFailedDescription'),
          });
        }

        return succeeded > 0;
      }

      return false;
    },
    [
      appTargetId,
      applyBulkNeoAttackResults,
      bulkNeoRun,
      canStartSignalAttack,
      failNeoAttacks,
      startNeoAttacks,
      tSignalsBulk,
      targetId,
      targetType,
    ],
  );

  const isSignalAttackRunning = useCallback(
    (signalId: string) => isRunningExecution(signalId),
    [isRunningExecution],
  );

  return {
    canStartSignalAttack,
    startSignalAttack,
    startBulkSignalAttack,
    isSignalAttackRunning,
    getAgentExecutionTabData,
  };
}
